import pushClient from './pushClient';
import type {
    PushDomain,
    PushDomainSetupResponse,
    PushCampaign,
    PushCampaignCreate,
    PushCampaignUpdate,
    PushCampaignPaginated,
    PushFlow,
    PushFlowCreate,
    PushFlowUpdate,
    PushFlowPaginated,
    PushFlowNode,
    PushFlowNodeCreate,
    PushFlowNodeUpdate,
    PushFlowReport,
    PushLeadsPaginated,
    PushLead,
    PushLeadStats,
    MessageResponse, PushTag,
    DashboardOverview,
    DashboardCampaignsReport,
    DashboardFlowsReport,
    TranslationLanguagesResponse,
    PushNotificationGroup,
    PushGroupCreate,
    PushGroupUpdate,
    PushDeliveriesPaginated,
    PushDeliveryByCampaign,
    PushPeriodFilter,
} from '@/types/push';

// ========== DOMAINS / SETUP ==========

export const getPushDomains = async (): Promise<PushDomain[]> => {
    const response = await pushClient.get<PushDomain[]>('/domains');
    return response.data;
};

export const getPushDomain = async (domainId: number): Promise<PushDomain> => {
    const response = await pushClient.get<PushDomain>(`/domains/${domainId}`);
    return response.data;
};

export const setupPushDomain = async (domainId: number): Promise<PushDomainSetupResponse> => {
    const response = await pushClient.post<PushDomainSetupResponse>(`/domains/${domainId}/setup`);
    return response.data;
};

export const regeneratePushScripts = async (domainId: number): Promise<{ success: boolean; message: string }> => {
    const response = await pushClient.post<{ success: boolean; message: string }>(`/domains/${domainId}/regenerate-scripts`);
    return response.data;
};

export const updatePushTriggerMode = async (domainId: number, triggerMode: 'auto' | 'manual'): Promise<{ success: boolean; message: string }> => {
    const response = await pushClient.put<{ success: boolean; message: string }>(`/domains/${domainId}/trigger-mode`, { trigger_mode: triggerMode });
    return response.data;
};

// ========== CAMPAIGNS ==========

export const getPushCampaigns = async (params?: {
    domain_id?: number;
    group_id?: number;
    status?: string;
    type?: string;
    page?: number;
    limit?: number;
}): Promise<PushCampaignPaginated> => {
    const response = await pushClient.get<PushCampaignPaginated>('/campaigns', { params });
    return response.data;
};

export const getPushCampaign = async (campaignId: number): Promise<PushCampaign> => {
    const response = await pushClient.get<PushCampaign>(`/campaigns/${campaignId}`);
    return response.data;
};

export const createPushCampaign = async (data: PushCampaignCreate): Promise<PushCampaign> => {
    const response = await pushClient.post<PushCampaign>('/campaigns', data);
    return response.data;
};

export const createPushCampaignBatch = async (campaigns: PushCampaignCreate[]): Promise<PushCampaign[]> => {
    const response = await pushClient.post<PushCampaign[]>('/campaigns/batch', { campaigns });
    return response.data;
};

export const updatePushCampaign = async (campaignId: number, data: PushCampaignUpdate): Promise<PushCampaign> => {
    const response = await pushClient.put<PushCampaign>(`/campaigns/${campaignId}`, data);
    return response.data;
};

export const cancelPushCampaign = async (campaignId: number): Promise<MessageResponse> => {
    const response = await pushClient.post<MessageResponse>(`/campaigns/${campaignId}/cancel`);
    return response.data;
};

export const resendPushCampaign = async (campaignId: number): Promise<PushCampaign> => {
    const response = await pushClient.post<PushCampaign>(`/campaigns/${campaignId}/resend`);
    return response.data;
};

// ========== FLOWS ==========

export const getPushFlows = async (params?: {
    domain_id?: number;
    group_id?: number;
    status?: string;
    page?: number;
    limit?: number;
}): Promise<PushFlowPaginated> => {
    const response = await pushClient.get<PushFlowPaginated>('/flows', { params });
    return response.data;
};

export const getPushFlow = async (flowId: number): Promise<PushFlow> => {
    const response = await pushClient.get<PushFlow>(`/flows/${flowId}`);
    return response.data;
};

export const createPushFlow = async (data: PushFlowCreate): Promise<PushFlow> => {
    const response = await pushClient.post<PushFlow>('/flows', data);
    return response.data;
};

export const updatePushFlow = async (flowId: number, data: PushFlowUpdate): Promise<PushFlow> => {
    const response = await pushClient.put<PushFlow>(`/flows/${flowId}`, data);
    return response.data;
};

export const deletePushFlow = async (flowId: number): Promise<MessageResponse> => {
    const response = await pushClient.delete<MessageResponse>(`/flows/${flowId}`);
    return response.data;
};

export const getPushFlowReport = async (flowId: number, start?: string, end?: string): Promise<PushFlowReport> => {
    const params: Record<string, string> = {};
    if (start) params.start = start;
    if (end) params.end = end;
    const response = await pushClient.get<PushFlowReport>(`/flows/${flowId}/report`, { params });
    return response.data;
};

// Flow Nodes
export const createPushFlowNode = async (data: PushFlowNodeCreate): Promise<PushFlowNode> => {
    const response = await pushClient.post<PushFlowNode>('/flows/nodes', data);
    return response.data;
};

export const updatePushFlowNode = async (nodeId: number, data: PushFlowNodeUpdate): Promise<MessageResponse> => {
    const response = await pushClient.put<MessageResponse>(`/flows/nodes/${nodeId}`, data);
    return response.data;
};

export const deletePushFlowNode = async (nodeId: number): Promise<MessageResponse> => {
    const response = await pushClient.delete<MessageResponse>(`/flows/nodes/${nodeId}`);
    return response.data;
};

export const reorderPushFlowNodes = async (nodes: { id: number; position: number }[]): Promise<MessageResponse> => {
    const response = await pushClient.post<MessageResponse>('/flows/nodes/reorder', { nodes });
    return response.data;
};

// ========== LEADS ==========

export const getPushLeads = async (domainId: number, params?: {
    page?: number;
    limit?: number;
    status?: string;
    period?: PushPeriodFilter;
    created_after?: string;
    created_before?: string;
}): Promise<PushLeadsPaginated> => {
    const response = await pushClient.get<PushLeadsPaginated>(`/leads/domain/${domainId}`, { params });
    return response.data;
};

export const getPushLead = async (subscriptionId: string): Promise<PushLead> => {
    const response = await pushClient.get<PushLead>(`/leads/${subscriptionId}`);
    return response.data;
};

export const getPushLeadStats = async (domainId?: number): Promise<PushLeadStats> => {
    const response = await pushClient.get<PushLeadStats>('/leads/stats', {
        params: domainId ? { domain_id: domainId } : undefined
    });
    return response.data;
};

export const addPushLeadTags = async (subscriptionId: string, tags: string[]): Promise<MessageResponse> => {
    const response = await pushClient.post<MessageResponse>(`/leads/${subscriptionId}/tags`, { tags });
    return response.data;
};

export const removePushLeadTags = async (subscriptionId: string, tags: string[]): Promise<MessageResponse> => {
    const response = await pushClient.delete<MessageResponse>(`/leads/${subscriptionId}/tags`, { data: { tags } });
    return response.data;
};

export const sendPushToLead = async (subscriptionId: string, payload: any, campaignId?: string): Promise<any> => {
    const response = await pushClient.post(`/leads/${subscriptionId}/send`, { payload, campaign_id: campaignId });
    return response.data;
};

// ========== DELIVERIES ==========

export const getPushLeadDeliveries = async (
    subscriptionId: string,
    params?: {
        page?: number;
        limit?: number;
        campaign_id?: string;
        kind?: 'campaign' | 'flow';
        success?: boolean;
        clicked?: boolean;
        period?: PushPeriodFilter;
    }
): Promise<PushDeliveriesPaginated> => {
    const response = await pushClient.get<PushDeliveriesPaginated>(
        `/leads/${subscriptionId}/deliveries`,
        { params }
    );
    return response.data;
};

export type DeliverabilitySortField =
    | 'campaign_name' | 'total' | 'delivered' | 'failed' | 'clicked' | 'ctr' | 'lastSentAt';
export type DeliverabilitySortOrder = 'asc' | 'desc';

export const getPushDomainDeliverability = async (
    domainId: number,
    params?: {
        period?: PushPeriodFilter;
        start?: string;
        end?: string;
        sort_by?: DeliverabilitySortField;
        sort_order?: DeliverabilitySortOrder;
        kind?: 'campaign' | 'flow';
        search?: string;
    }
): Promise<PushDeliveryByCampaign[]> => {
    const response = await pushClient.get<PushDeliveryByCampaign[]>(
        `/leads/domain/${domainId}/deliverability`,
        { params }
    );
    return response.data;
};

export const getPushDomainDeliveries = async (
    domainId: number,
    params?: {
        page?: number;
        limit?: number;
        campaign_id?: string;
        kind?: 'campaign' | 'flow';
        success?: boolean;
        period?: PushPeriodFilter;
        start?: string;
        end?: string;
    }
): Promise<PushDeliveriesPaginated> => {
    const response = await pushClient.get<PushDeliveriesPaginated>(
        `/leads/domain/${domainId}/deliveries`,
        { params }
    );
    return response.data;
};

export const getPushCampaignDeliveries = async (
    campaignId: string,
    params?: {
        page?: number;
        limit?: number;
        success?: boolean;
        clicked?: boolean;
        period?: PushPeriodFilter;
        start?: string;
        end?: string;
    }
): Promise<PushDeliveriesPaginated> => {
    const response = await pushClient.get<PushDeliveriesPaginated>(
        `/campaigns/${campaignId}/leads`,
        { params }
    );
    return response.data;
};

export const getPushTags = async (domainId: number): Promise<PushTag[]> => {
    const response = await pushClient.get<PushTag[]>(`/tags/domain/${domainId}`);
    return response.data;
};

export const createPushTag = async (data: {
    domain_id: number; name: string; color: string; description?: string;
}): Promise<PushTag> => {
    const response = await pushClient.post<PushTag>('/tags', data);
    return response.data;
};

// ========== GROUPS ==========

export const getPushGroups = async (): Promise<PushNotificationGroup[]> => {
    const response = await pushClient.get<PushNotificationGroup[]>('/groups');
    return response.data;
};

export const getPushGroup = async (groupId: number): Promise<PushNotificationGroup> => {
    const response = await pushClient.get<PushNotificationGroup>(`/groups/${groupId}`);
    return response.data;
};

export const createPushGroup = async (data: PushGroupCreate): Promise<PushNotificationGroup> => {
    const response = await pushClient.post<PushNotificationGroup>('/groups', data);
    return response.data;
};

export const updatePushGroup = async (groupId: number, data: PushGroupUpdate): Promise<PushNotificationGroup> => {
    const response = await pushClient.put<PushNotificationGroup>(`/groups/${groupId}`, data);
    return response.data;
};

export const deletePushGroup = async (groupId: number): Promise<MessageResponse> => {
    const response = await pushClient.delete<MessageResponse>(`/groups/${groupId}`);
    return response.data;
};

export const addDomainsToGroup = async (groupId: number, domainIds: number[]): Promise<PushNotificationGroup> => {
    const response = await pushClient.post<PushNotificationGroup>(`/groups/${groupId}/domains`, { domain_ids: domainIds });
    return response.data;
};

export const removeDomainsFromGroup = async (groupId: number, domainIds: number[]): Promise<PushNotificationGroup> => {
    const response = await pushClient.delete<PushNotificationGroup>(`/groups/${groupId}/domains`, { data: { domain_ids: domainIds } });
    return response.data;
};

// ========== TRANSLATIONS ==========

export const getTranslationLanguages = async (): Promise<TranslationLanguagesResponse> => {
    const response = await pushClient.get<TranslationLanguagesResponse>('/translations/languages');
    return response.data;
};

// ========== DASHBOARD ==========

export const getPushDashboardOverview = async (params: {
    domain_id?: number; start: string; end: string;
}): Promise<DashboardOverview> => {
    const response = await pushClient.get<DashboardOverview>('/dashboard', { params });
    return response.data;
};

export const getPushDashboardCampaigns = async (params: {
    domain_id?: number; start: string; end: string; campaign_id?: number;
}): Promise<DashboardCampaignsReport> => {
    const response = await pushClient.get<DashboardCampaignsReport>('/dashboard/campaigns', { params });
    return response.data;
};

export const getPushDashboardFlows = async (params: {
    domain_id?: number; start: string; end: string; flow_id?: number;
}): Promise<DashboardFlowsReport> => {
    const response = await pushClient.get<DashboardFlowsReport>('/dashboard/flows', { params });
    return response.data;
};

const PUSH_MS_URL = 'https://push.rocketgroup.io';

export const unsubscribeLead = async (lead: { _id: string; endpoint?: string }): Promise<any> => {
    // Tenta unsubscribe pelo endpoint (rota pública)
    if (lead.endpoint) {
        try {
            const response = await fetch(`${PUSH_MS_URL}/unsubscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ endpoint: lead.endpoint }),
            });
            if (response.ok) return response.json();
        } catch (e) {
            // fallback
        }
    }

    // Fallback: delete pelo ID no MS de envio
    const response = await fetch(`${PUSH_MS_URL}/subscriptions/${lead._id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer nsjidnaijsdnigbasnibieuwnginviq`
        },
    });
    if (!response.ok) throw new Error('Erro ao desativar lead');
    return response.status === 204 ? { success: true } : response.json();
};